import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface PayoutManagementProps {
  userId: string;
}

interface PayoutRequest {
  id: string;
  amount: number;
  earnings_amount: number;
  tips_amount: number;
  status: string;
  requested_at: string;
  completed_at: string | null;
  stripe_transfer_id: string | null;
}

interface ConnectStatus {
  connected: boolean;
  status: string;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  requirementsCurrentlyDue?: string[];
}

export default function PayoutManagement({ userId }: PayoutManagementProps) {
  const [balance, setBalance] = useState(0);
  const [tipsBalance, setTipsBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalTips, setTotalTips] = useState(0);
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [amount, setAmount] = useState('');
  const [includeEarnings, setIncludeEarnings] = useState(true);
  const [includeTips, setIncludeTips] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Stripe Connect states
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [autoPayoutEnabled, setAutoPayoutEnabled] = useState(false);
  const [autoPayoutMinAmount, setAutoPayoutMinAmount] = useState(50);

  useEffect(() => {
    loadPayoutData();
    checkConnectStatus();
  }, [userId]);

  const loadPayoutData = async () => {
    try {
      // Get provider profile
      const { data: provider } = await supabase
        .from('provider_profiles')
        .select('balance, tips_balance, total_earnings, total_tips, auto_payout_enabled, auto_payout_min_amount')
        .eq('user_id', userId)
        .single();

      if (provider) {
        setBalance(provider.balance || 0);
        setTipsBalance(provider.tips_balance || 0);
        setTotalEarnings(provider.total_earnings || 0);
        setTotalTips(provider.total_tips || 0);
        setAutoPayoutEnabled(provider.auto_payout_enabled || false);
        setAutoPayoutMinAmount(provider.auto_payout_min_amount || 50);
      }

      // Get payout history (create table if doesn't exist)
      const { data: payouts } = await supabase
        .from('payouts')
        .select('*')
        .eq('provider_id', userId)
        .order('requested_at', { ascending: false })
        .limit(20);

      if (payouts) {
        setPayoutRequests(payouts);
      }
    } catch (err) {
      console.error('Error loading payout data:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkConnectStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-stripe-connect-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      const result = await response.json();
      setConnectStatus(result);
    } catch (err) {
      console.error('Error checking Connect status:', err);
    }
  };

  const handleConnectOnboarding = async () => {
    setLoadingConnect(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-stripe-connect-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/provider-dashboard?tab=payouts&setup=complete`,
            refreshUrl: `${window.location.origin}/provider-dashboard?tab=payouts&refresh=true`,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create Connect account');
      }

      // Redirect to Stripe onboarding
      window.location.href = result.onboardingUrl;
    } catch (err: any) {
      console.error('Error starting Connect onboarding:', err);
      setError(err.message || 'Failed to start onboarding');
    } finally {
      setLoadingConnect(false);
    }
  };

  const handleToggleAutoPayout = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/toggle-auto-payout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            enabled: !autoPayoutEnabled,
            minAmount: autoPayoutMinAmount,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to toggle auto-payout');
      }

      setAutoPayoutEnabled(!autoPayoutEnabled);
      setSuccess(result.message);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to toggle auto-payout');
    }
  };

  const handleRequestPayout = async () => {
    const requestAmount = parseFloat(amount);

    if (!requestAmount || requestAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const availableBalance = (includeEarnings ? balance : 0) + (includeTips ? tipsBalance : 0);

    if (requestAmount > availableBalance) {
      setError(`Insufficient balance. Available: £${availableBalance.toFixed(2)}`);
      return;
    }

    if (requestAmount < 50) {
      setError('Minimum payout amount is £50');
      return;
    }

    if (!includeEarnings && !includeTips) {
      setError('Please select at least one balance type');
      return;
    }

    setRequesting(true);
    setError('');
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/request-payout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            amount: requestAmount,
            includeEarnings,
            includeTips,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (result.needsSetup) {
          setError('Please complete Stripe Connect setup first');
          return;
        }
        throw new Error(result.error || 'Failed to request payout');
      }

      setSuccess('Payout request submitted successfully! Funds will be transferred within 2-3 business days.');
      setAmount('');
      await loadPayoutData();
    } catch (err: any) {
      console.error('Error requesting payout:', err);
      setError(err.message || 'Failed to request payout');
    } finally {
      setRequesting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getConnectStatusBadge = () => {
    if (!connectStatus) return null;

    if (!connectStatus.connected || connectStatus.status === 'not_started') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
          Not Connected
        </span>
      );
    }

    switch (connectStatus.status) {
      case 'active':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            ✓ Active
          </span>
        );
      case 'pending':
      case 'incomplete':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
            Incomplete
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
            {connectStatus.status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
        <p className="mt-4 text-gray-600">Loading payout information...</p>
      </div>
    );
  }

  const availableBalance = (includeEarnings ? balance : 0) + (includeTips ? tipsBalance : 0);
  const isConnectActive = connectStatus?.status === 'active';

  return (
    <div className="space-y-6">
      {/* Stripe Connect Status */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Stripe Connect Status</h3>
            <p className="text-sm text-gray-600 mt-1">Manage your payout account</p>
          </div>
          {getConnectStatusBadge()}
        </div>

        {!isConnectActive ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
              <div className="flex items-start">
                <i className="ri-information-line text-yellow-500 text-xl mr-3 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-yellow-900 mb-1">
                    Connect Your Bank Account
                  </p>
                  <p className="text-sm text-yellow-800">
                    To receive payouts, you need to complete Stripe Connect onboarding. This is a secure process that takes about 5 minutes.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleConnectOnboarding}
              disabled={loadingConnect}
              className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold whitespace-nowrap"
            >
              {loadingConnect ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="ri-bank-line mr-2"></i>
                  Connect Bank Account
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
              <div className="flex items-start">
                <i className="ri-check-circle-line text-green-500 text-xl mr-3 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-green-900 mb-1">
                    Bank Account Connected
                  </p>
                  <p className="text-sm text-green-800">
                    Your Stripe Connect account is active. You can now request payouts.
                  </p>
                </div>
              </div>
            </div>

            {/* Auto-Payout Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Weekly Auto-Payout</p>
                <p className="text-sm text-gray-600">
                  Automatically withdraw when balance reaches £{autoPayoutMinAmount}
                </p>
              </div>
              <button
                onClick={handleToggleAutoPayout}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoPayoutEnabled ? 'bg-teal-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoPayoutEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Balance Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold opacity-90">Job Earnings</h3>
            <i className="ri-wallet-3-line text-3xl opacity-80"></i>
          </div>
          <p className="text-4xl font-bold mb-2">£{balance.toFixed(2)}</p>
          <p className="text-sm opacity-80">Total earned: £{totalEarnings.toFixed(2)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold opacity-90">Tips Balance</h3>
            <i className="ri-gift-line text-3xl opacity-80"></i>
          </div>
          <p className="text-4xl font-bold mb-2">£{tipsBalance.toFixed(2)}</p>
          <p className="text-sm opacity-80">Total tips: £{totalTips.toFixed(2)}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold opacity-90">Available Now</h3>
            <i className="ri-money-pound-circle-line text-3xl opacity-80"></i>
          </div>
          <p className="text-4xl font-bold mb-2">£{availableBalance.toFixed(2)}</p>
          <p className="text-sm opacity-80">Ready to withdraw</p>
        </div>
      </div>

      {/* Request Payout */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Request Payout</h3>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-500 mr-2"></i>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 mr-2"></i>
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Balance Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Balance Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeEarnings}
                  onChange={(e) => setIncludeEarnings(e.target.checked)}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">
                  Job Earnings (£{balance.toFixed(2)})
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeTips}
                  onChange={(e) => setIncludeTips(e.target.checked)}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">
                  Tips (£{tipsBalance.toFixed(2)})
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (£)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              min="50"
              max={availableBalance}
              step="0.01"
              disabled={!isConnectActive}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum: £50 | Maximum: £{availableBalance.toFixed(2)}
            </p>
          </div>

          <button
            onClick={handleRequestPayout}
            disabled={requesting || availableBalance < 50 || !isConnectActive}
            className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold whitespace-nowrap"
          >
            {requesting ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="ri-bank-line mr-2"></i>
                Request Payout
              </>
            )}
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <i className="ri-information-line"></i>
            Payout Information
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Minimum payout amount: £50</li>
            <li>• Payouts are processed within 2-3 business days</li>
            <li>• Funds will be transferred to your connected bank account</li>
            <li>• You can withdraw job earnings and tips separately or together</li>
            <li>• Enable auto-payout for weekly automatic withdrawals</li>
          </ul>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Payout History</h3>

        {payoutRequests.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-history-line text-5xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">No payout requests yet</p>
            <p className="text-sm text-gray-500 mt-2">Your payout history will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payoutRequests.map((payout) => (
              <div
                key={payout.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-lg font-bold text-gray-900">
                      £{payout.amount.toFixed(2)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(payout.status)}`}>
                      {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {payout.earnings_amount > 0 && (
                      <p>Earnings: £{payout.earnings_amount.toFixed(2)}</p>
                    )}
                    {payout.tips_amount > 0 && (
                      <p>Tips: £{payout.tips_amount.toFixed(2)}</p>
                    )}
                    <p>Requested: {new Date(payout.requested_at).toLocaleString()}</p>
                    {payout.completed_at && (
                      <p>Completed: {new Date(payout.completed_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {payout.status === 'completed' && (
                    <i className="ri-check-circle-fill text-2xl text-green-500"></i>
                  )}
                  {payout.status === 'pending' && (
                    <i className="ri-time-line text-2xl text-yellow-500"></i>
                  )}
                  {payout.status === 'processing' && (
                    <i className="ri-loader-4-line text-2xl text-blue-500 animate-spin"></i>
                  )}
                  {payout.status === 'failed' && (
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}